import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ApiRequestError,
  approveCloseReview,
  getCloseReview,
  getCloseReviews,
  invokeExtensionV2,
  listEntityReportLocations,
  listExtensionInstallations,
  returnCloseReview,
  reverseClosePosting,
  saveCloseReview,
  type CloseReview,
  type CloseReviewDetail,
  type CloseReviewPosting,
  type EntityReportLocation,
  type ExtensionInstallation,
} from "@/lib/api";
import { useOrganization } from "@/lib/organization";
import { capabilityAllowed, useCultivationCapabilities } from "./cultivation-capabilities";
import { monthBounds } from "./cultivation-dates";
import { correctionMetadataFromValues, type CultivationApprovalSummary } from "./cultivation-review";
import type { CultivationImports, CultivationWorkspace } from "./cultivation-types";

function messageOf(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function isApprovalStale(cause: unknown): boolean {
  return cause instanceof ApiRequestError &&
    cause.status === 409 &&
    cause.message.includes("approval_stale");
}

function searchString(search: Record<string, unknown>, key: string): string {
  const value = search[key];
  return typeof value === "string" ? value : "";
}

function validDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(Date.parse(`${value}T00:00:00Z`));
}

interface ReviewData {
  scopeKey: string;
  reviews: CloseReview[];
  postings: CloseReviewPosting[];
}

interface ReviewDetailData {
  scopeKey: string;
  reviewId: string;
  detail: CloseReviewDetail | null;
}

export function useCultivationWorkspace(): CultivationWorkspace {
  const { scope } = useOrganization();
  const navigate = useNavigate();
  const search = useRouterState({ select: (state) => state.location.search }) as Record<string, unknown>;
  const defaults = useMemo(() => monthBounds(), []);
  const [installations, setInstallations] = useState<ExtensionInstallation[]>([]);
  const [locations, setLocations] = useState<EntityReportLocation[]>([]);
  const [reviewData, setReviewData] = useState<ReviewData>({ scopeKey: "", reviews: [], postings: [] });
  const [detailData, setDetailData] = useState<ReviewDetailData>({ scopeKey: "", reviewId: "", detail: null });
  const [imports, setImports] = useState<CultivationImports>({ count: 0, imports: [], attestations: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const capabilityState = useCultivationCapabilities(scope?.legal_entity_id ?? null);

  const requestedLicense = searchString(search, "license_id");
  const licenseId = locations.some((location) => location.id === requestedLicense)
    ? requestedLicense
    : locations.length === 1
      ? locations[0]!.id
      : "";
  const requestedStart = searchString(search, "period_start");
  const requestedEnd = searchString(search, "period_end");
  const periodStart = validDate(requestedStart) ? requestedStart : defaults.start;
  const periodEnd = validDate(requestedEnd) ? requestedEnd : defaults.end;
  const scopeKey = [
    scope?.organization_id ?? "",
    scope?.legal_entity_id ?? "",
    licenseId,
    periodStart,
    periodEnd,
  ].join("|");
  const scopeKeyRef = useRef(scopeKey);
  scopeKeyRef.current = scopeKey;
  const reviewsToken = useRef(0);
  const detailToken = useRef(0);
  const importsToken = useRef(0);
  const viewAllowed = !capabilityState.loading && capabilityAllowed(capabilityState.capabilities, "view");

  const installation = useMemo(
    () => installations.find(
      (item) =>
        item.state === "enabled" &&
        item.publisher === "sylviq" &&
        item.key === "niche-cultivation-costing" &&
        item.api_version === "2.0" &&
        item.apps?.includes("niche-accounting"),
    ) ?? null,
    [installations],
  );

  const reviews = reviewData.scopeKey === scopeKey ? reviewData.reviews : [];
  const postings = reviewData.scopeKey === scopeKey ? reviewData.postings : [];
  const currentReview = useMemo(
    () => installation
      ? reviews.find((review) => review.installation_id === installation.id) ?? null
      : null,
    [installation, reviews],
  );
  const reviewDetail = detailData.scopeKey === scopeKey &&
    detailData.reviewId === currentReview?.review_id
    ? detailData.detail
    : null;

  const updateSearch = useCallback((patch: Record<string, string>) => {
    void navigate({
      to: ".",
      search: (previous: Record<string, unknown>) => ({ ...previous, ...patch }),
      replace: true,
    });
  }, [navigate]);

  const setLicense = useCallback((value: string) => {
    updateSearch({ license_id: value });
  }, [updateSearch]);

  const setPeriod = useCallback((start: string, end: string) => {
    updateSearch({ period_start: start, period_end: end });
  }, [updateSearch]);

  useEffect(() => {
    if (!scope?.organization_id || !scope.legal_entity_id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      listExtensionInstallations(scope.organization_id),
      listEntityReportLocations(scope.organization_id, scope.legal_entity_id),
    ])
      .then(([nextInstallations, nextLocations]) => {
        if (cancelled) return;
        setInstallations(nextInstallations);
        setLocations(nextLocations);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(messageOf(cause));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scope?.legal_entity_id, scope?.organization_id]);

  useEffect(() => {
    reviewsToken.current += 1;
    detailToken.current += 1;
    importsToken.current += 1;
    setReviewData({ scopeKey, reviews: [], postings: [] });
    setDetailData({ scopeKey, reviewId: "", detail: null });
  }, [scopeKey]);

  const reloadReviews = useCallback(async () => {
    const capturedScope = scopeKey;
    const token = ++reviewsToken.current;
    if (
      !viewAllowed ||
      !scope?.legal_entity_id ||
      !licenseId ||
      !periodStart ||
      !periodEnd
    ) {
      if (scopeKeyRef.current === capturedScope) {
        setReviewData({ scopeKey: capturedScope, reviews: [], postings: [] });
      }
      return;
    }
    const result = await getCloseReviews({
      legal_entity_id: scope.legal_entity_id,
      license_id: licenseId,
      period_start: periodStart,
      period_end: periodEnd,
    });
    if (token !== reviewsToken.current || scopeKeyRef.current !== capturedScope) return;
    setReviewData({ scopeKey: capturedScope, reviews: result.reviews, postings: result.postings });
  }, [licenseId, periodEnd, periodStart, scope?.legal_entity_id, scopeKey, viewAllowed]);

  const loadReviewDetail = useCallback(async (reviewId: string): Promise<CloseReviewDetail | null> => {
    const capturedScope = scopeKeyRef.current;
    const token = ++detailToken.current;
    if (!viewAllowed || !reviewId) {
      setDetailData({ scopeKey: capturedScope, reviewId, detail: null });
      return null;
    }
    try {
      const detail = await getCloseReview(reviewId);
      if (token === detailToken.current && scopeKeyRef.current === capturedScope) {
        setDetailData({ scopeKey: capturedScope, reviewId, detail });
      }
      return detail;
    } catch (cause) {
      if (token === detailToken.current && scopeKeyRef.current === capturedScope) {
        setDetailData({ scopeKey: capturedScope, reviewId, detail: null });
      }
      throw cause;
    }
  }, [viewAllowed]);

  const reloadReviewDetail = useCallback(async () => {
    if (!currentReview?.review_id) {
      setDetailData({ scopeKey: scopeKeyRef.current, reviewId: "", detail: null });
      return;
    }
    await loadReviewDetail(currentReview.review_id);
  }, [currentReview?.review_id, loadReviewDetail]);

  useEffect(() => {
    if (!currentReview?.review_id) {
      setDetailData({ scopeKey, reviewId: "", detail: null });
      return;
    }
    let cancelled = false;
    if (!viewAllowed) return;
    void loadReviewDetail(currentReview.review_id).catch(() => {
      if (!cancelled) setDetailData({ scopeKey, reviewId: currentReview.review_id, detail: null });
    });
    return () => {
      cancelled = true;
    };
  }, [currentReview?.review_id, currentReview?.review_revision, loadReviewDetail, scopeKey, viewAllowed]);

  useEffect(() => {
    let cancelled = false;
    if (!viewAllowed || !scope?.legal_entity_id || !licenseId || !periodStart || !periodEnd) {
      setReviewData({ scopeKey, reviews: [], postings: [] });
      return;
    }
    void reloadReviews().catch((cause: unknown) => {
      if (!cancelled) setError(messageOf(cause));
    });
    return () => {
      cancelled = true;
    };
  }, [licenseId, periodEnd, periodStart, reloadReviews, scope?.legal_entity_id, scopeKey, viewAllowed]);

  const reloadImports = useCallback(async () => {
    const capturedScope = scopeKey;
    const token = ++importsToken.current;
    if (!viewAllowed || !scope?.organization_id || !installation) {
      if (scopeKeyRef.current === capturedScope) {
        setImports({ count: 0, imports: [], attestations: [] });
      }
      return;
    }
    const response = await invokeExtensionV2<CultivationImports>(
      scope.organization_id,
      installation.id,
      {
        kind: "ui_command",
        app: "niche-accounting",
        payload: {
          command: "niche.metrc.import.list",
          values: {},
          viewId: "costing",
          selectedIds: [],
          renderToken: crypto.randomUUID(),
        },
      },
    );
    if (!response.ok || !response.result) {
      throw new Error(response.error?.message ?? "METRC import history is unavailable.");
    }
    if (token !== importsToken.current || scopeKeyRef.current !== capturedScope) return;
    setImports(response.result);
  }, [installation, scope?.organization_id, scopeKey, viewAllowed]);

  useEffect(() => {
    if (!installation || !viewAllowed) {
      setImports({ count: 0, imports: [], attestations: [] });
      return;
    }
    void reloadImports().catch(() => {
      setImports({ count: 0, imports: [], attestations: [] });
    });
  }, [installation, reloadImports, viewAllowed]);

  const saveReviewedInputs = useCallback(async (
    inputs: Record<string, unknown>,
    formValues?: Record<string, string>,
    approval?: CultivationApprovalSummary,
  ) => {
    if (!scope?.legal_entity_id) throw new Error("The books context is unavailable.");
    if (!installation) throw new Error("The cultivation costing extension is not installed.");
    if (!licenseId || !periodStart || !periodEnd) {
      throw new Error("Choose a location and period before reviewing inputs.");
    }
    const capturedScope = scopeKeyRef.current;
    const correction = correctionMetadataFromValues(formValues ?? {});
    const saved = await saveCloseReview({
      legal_entity_id: scope.legal_entity_id,
      installation_id: installation.id,
      license_id: licenseId,
      period_start: periodStart,
      period_end: periodEnd,
      close_inputs: inputs,
      ...(formValues ? { form_values: formValues } : {}),
      ...(approval ? {
        approval_required: approval.approval_required,
        approval_reasons: approval.reasons,
      } : {}),
      ...(correction.correction_kind ? {
        correction_kind: correction.correction_kind,
        correction_reason: correction.correction_reason,
        ...(correction.correction_of_review_id ? { correction_of_review_id: correction.correction_of_review_id } : {}),
      } : {}),
    });
    if (scopeKeyRef.current === capturedScope) {
      setReviewData((current) => {
        if (current.scopeKey !== capturedScope) return current;
        const reviews = current.reviews.filter(
          (review) => review.review_id !== saved.review.review_id &&
            review.installation_id !== saved.review.installation_id,
        );
        return { ...current, reviews: [...reviews, saved.review] };
      });
      if (formValues) {
        setDetailData({
          scopeKey: capturedScope,
          reviewId: saved.review.review_id,
          detail: { review: saved.review, form_values: formValues },
        });
      }
    }
    // The save already succeeded. A refresh failure must not turn it into an
    // error or discard the optimistic review above.
    await Promise.allSettled([reloadReviews(), reloadImports()]);
  }, [installation, licenseId, periodEnd, periodStart, reloadImports, reloadReviews, scope?.legal_entity_id]);

  const approveReview = useCallback(async (reviewId: string, note: string) => {
    const review = reviews.find((item) => item.review_id === reviewId);
    if (!review?.input_sha256) {
      throw new Error("Reload the review before approving the current revision.");
    }
    try {
      await approveCloseReview(reviewId, {
        expected_input_sha256: review.input_sha256,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
    } catch (cause) {
      if (isApprovalStale(cause)) {
        await Promise.allSettled([reloadReviews(), loadReviewDetail(reviewId)]);
        throw new Error(
          "The saved inputs changed before this approval was recorded. The latest review was reloaded; review it and approve the current revision again.",
        );
      }
      throw cause;
    }
    await Promise.allSettled([reloadReviews(), loadReviewDetail(reviewId)]);
  }, [loadReviewDetail, reloadReviews, reviews]);

  const returnReview = useCallback(async (reviewId: string, note: string) => {
    const review = reviews.find((item) => item.review_id === reviewId);
    try {
      await returnCloseReview(reviewId, {
        note,
        ...(review?.input_sha256 ? { expected_input_sha256: review.input_sha256 } : {}),
      });
    } catch (cause) {
      if (isApprovalStale(cause)) {
        await Promise.allSettled([reloadReviews(), loadReviewDetail(reviewId)]);
        throw new Error(
          "The saved inputs changed before this return was recorded. The latest review was reloaded; review it and try again.",
        );
      }
      throw cause;
    }
    await Promise.allSettled([reloadReviews(), loadReviewDetail(reviewId)]);
  }, [loadReviewDetail, reloadReviews, reviews]);

  const reversePosting = useCallback(async (
    posting: CloseReviewPosting,
    reason: string,
    date: string,
  ) => {
    if (!scope?.legal_entity_id) throw new Error("The books context is unavailable.");
    const capturedScope = scopeKeyRef.current;
    const result = await reverseClosePosting(posting.posting_id, {
      legal_entity_id: scope.legal_entity_id,
      reversal_date: date,
      reason,
    });
    if (scopeKeyRef.current === capturedScope) {
      setReviewData({ scopeKey: capturedScope, reviews: result.reviews, postings: result.postings });
    }
  }, [scope?.legal_entity_id]);

  return {
    organizationId: scope?.organization_id ?? "",
    installation,
    locations,
    licenseId,
    periodStart,
    periodEnd,
    reviews,
    postings,
    reviewDetail,
    imports,
    capabilities: capabilityState.capabilities,
    capabilitiesLoading: capabilityState.loading,
    capabilitiesError: capabilityState.error,
    loading,
    error,
    setLicense,
    setPeriod,
    reloadReviews,
    reloadReviewDetail,
    reloadImports,
    reloadCapabilities: capabilityState.reload,
    saveReviewedInputs,
    approveReview,
    returnReview,
    reversePosting,
  };
}
